import * as XLSX from "xlsx";

import { Icon, Tooltip } from "../../../common/MaterialComponent";
import React, { useEffect, useRef, useState } from "react";

import { ButtonPrimary } from "../../../common/sharedStyle";
import { GetExcelDataColumn } from "../../../hooks/useBatchGroupConfiguration";
import { GroupDetail } from "../../../models/BatchMessage/BatchMessageGroupDetailModel";
import { IExcel } from "../../../common/batchNotificationInterface";
import Shared from "../../../common/shared";
import styles from "../GroupConfiguration/GroupConfiguration.style";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface ExcelFileUploadProp {
	dynamicVariables: string[];
	rowId: string;
	excelData: IExcel;
	groupDetail: GroupDetail;
	isReadOnly: boolean;
	onExcelDataUpdate: (excelData: IExcel) => void;
	highlightRequiredDetail: Boolean;
}

export const ExcelFileUpload: React.FC<ExcelFileUploadProp> = ({
	dynamicVariables,
	rowId,
	excelData,
	groupDetail,
	isReadOnly,
	onExcelDataUpdate,
	highlightRequiredDetail,
}) => {
	const strings = useGetComponentStringsContext().ExcelFileUpload;
	const classes = styles();
	const [isFileInputTouched, setIsFileInputTouched] = useState<boolean>(false);
	const [
		isFileTypeExtensionError,
		setIsFileTypeExtensionError,
	] = useState<boolean>(false);
	const excelFileUpload = useRef<HTMLInputElement | null>(null);

	const excelTemplateUrl = process.env.REACT_APP_EXCEL_TEMPLATE_URL;

	useEffect(() => {
		if (highlightRequiredDetail) {
			setIsFileInputTouched(true);
		}
	}, [highlightRequiredDetail]);

	// handle file uploading
	const onUploadFileButtonClick = (id: string) => {
		if (isReadOnly) {
			return;
		}
		setIsFileTypeExtensionError(false);
		if (excelFileUpload && excelFileUpload.current) {
			excelFileUpload.current.click();
		}
	};

	const onUploadFileHandleClick = (e: any, index: string): any => {
		const files = e.target.files;
		const rawFile = files[0]; // only use files[0]
		if (!rawFile) {
			return;
		}
		setIsFileInputTouched(true);
		if (!isExcelOrCSV(rawFile)) {
			setIsFileTypeExtensionError(true);
			return;
		}
		readDataFromFile(rawFile);
	};

	const isExcelOrCSV = (rawFile: any) => {
		return /\.(xlsx|xls|csv)$/.test(rawFile.name);
	};

	const readDataFromFile = (rawFile: any) => {
		return new Promise((resolve: any) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				const data = e.target!.result;
				const workbook = XLSX.read(data, { type: "array" });
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				const header = getHeaderRow(worksheet);
				const results = XLSX.utils.sheet_to_json(worksheet, {
					header: 0,
					defval: "",
				});
				if (header && header.length > 0 && results && results.length > 0) {
					generateData({ header, results });
				}
				resolve();
				// reset input file ref value
				if (excelFileUpload && excelFileUpload.current) {
					excelFileUpload.current.value = "";
				}
			};
			reader.readAsArrayBuffer(rawFile);
		});
	};

	const getHeaderRow = (sheet: any) => {
		const headers: string[] = [];
		try {
			const range = XLSX.utils.decode_range(sheet["!ref"]);
			let C;
			const R = range.s.r;
			/* start in the first row */
			for (C = range.s.c; C <= range.e.c; ++C) {
				/* walk every column in the range */
				const cell = sheet[XLSX.utils.encode_cell({ c: C, r: R })];
				/* find the cell in the first row */
				let hdr = "UNKNOWN " + C;
				if (cell && cell.t) {
					hdr = XLSX.utils.format_cell(cell);
				}
				headers.push(hdr);
			}
		} catch (e) {
			Shared.showSnackbarMessage(strings.uploadErrorSnackbarMessage);
			return;
		}
		return headers;
	};

	const generateData = ({ header, results }: any) => {
		const excelDataObject: IExcel = {
			id: rowId,
			header,
			contentData: results,
			variableMapping: dynamicVariables.reduce(
				(accumulator, key) => ({ ...accumulator, [key]: key }),
				{}
			),
			isHeaderIncluded: false,
			selectedColumn: "Column 1",
		};
		excelDataObject.staticHeader = generateDynamicColumn(
			excelDataObject.header
		);
		excelDataObject.contentData = GetExcelDataColumn(excelDataObject);
		onExcelDataUpdate(excelDataObject);
	};

	const generateDynamicColumn = (headerList: string[]): any => {
		const tempHeader: string[] = headerList;
		const dynamicGenerateColumnName: string[] = [];
		for (let i = 0; i < tempHeader.length; i++) {
			dynamicGenerateColumnName.push("Column " + (i + 1));
		}
		return dynamicGenerateColumnName;
	};

	function getErrorMessage() {
		return isFileTypeExtensionError
			? strings.fileTypeErrorText
			: highlightRequiredDetail && groupDetail.phoneNumberCount === 0
			? strings.fileUploadValidationMessage
			: "";
	}

	return (
		<>
			<Tooltip title={strings.uploadFileToolTip}>
				<span>
					<ButtonPrimary
						variant="contained"
						color="primary"
						onClick={() => onUploadFileButtonClick(excelData.id)}
						className={classes.fileUploadBtn}
						data-testid="fileUploadBtn"
						disabled={isReadOnly}
					>
						<Icon>file_upload</Icon>
					</ButtonPrimary>
				</span>
			</Tooltip>
			<span className={classes.fileName}>
				<b>
					{groupDetail.phoneNumberCount !== 0
						? `${strings.numberOfRecordsText} ${
								groupDetail.phoneNumberListURL
									? strings.uploadedLabel
									: strings.addedLabel
						  }: ${groupDetail.phoneNumberCount}`
						: `${strings.noFileSelectedText} `}
				</b>
			</span>
			{excelTemplateUrl && (
				<span className={classes.excelTemplateUrlText}>
					{strings.clickText}{" "}
					<a href={excelTemplateUrl} target="_blank" rel="noopener noreferrer">
						{strings.hereText}
					</a>{" "}
					{strings.excelTemplateManualText}
				</span>
			)}
			<input
				type="file"
				id={"file" + excelData.id}
				ref={excelFileUpload}
				onChange={(event: any) => {
					onUploadFileHandleClick(event, excelData.id);
				}}
				className={classes.fileInput}
				accept={Shared.excelMimeTypeList.join(",")}
			/>

			<div>
				{(highlightRequiredDetail
					? true
					: groupDetail.phoneNumberCount === 0 || isFileInputTouched) &&
					getErrorMessage() && (
						<div className={classes.errorMessage}>{getErrorMessage()}</div>
					)}
			</div>
		</>
	);
};
